import React, { useContext, useMemo } from 'react';
import { shape, arrayOf } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import {
  useProductsFullfilmentValidation,
  useGetCartItems,
  getCartData,
  expandAccessoryQuantities
} from '@thd-olt-component-react/product-bundle';
import { tiggerAnalyticsCustomEvent } from '../../utils/product-utils';
import { LAUNDRY_ADD_TO_CART } from '../../constants/index';
import { BundleMiniContext } from '../../context/BundleMiniContext';

/**
 * LaundryAddToCart component renders the Add to Cart button for a laundry bundle.
 * It uses the useProductsFullfilmentValidation and useGetCartItems hooks
 * to fetch the necessary cart data and fulfillment information.
*
* @param {Object} props - The props object.
* @param {Object} props.bundle - The bundle object containing pricing details.
* @param {Array} props.products - An array of products including washer, dryer, and accessories.
* @param {Array} props.accessories - An array of accessory products
*
* @returns {JSX.Element} The rendered LaundryAddToCart component.
*/

export const LaundryAddToCart = ({ bundle, products, accessories }) => {
  const experienceContext = useContext(ExperienceContext);
  const { itemId } = useContext(BundleMiniContext) || {};
  const { store, deliveryZip } = experienceContext || {};
  const { storeId, storeZip } = store || {};
  const normalizedAccessories = expandAccessoryQuantities(accessories);
  const bundleProducts = useMemo(() =>
    [...new Set([...products, ...(normalizedAccessories || [])])],
  [products, accessories]
  );

  const { productsFulfillmentInformation } = useProductsFullfilmentValidation({
    products: bundleProducts,
    deliveryZip,
    bundleQuantity: 1,
    storeZip,
    quantityLimitInventory: 1,
    addToCartFlag: false,
    disableATCForMissingProductsFlag: false
  });
  const { cartItems } = useGetCartItems({
    productsFulfillmentInformation,
    bundleQuantity: 1,
    storeId,
    storeZip,
    deliveryZip,
    bundleId: bundle?.itemId,
    experienceType: bundle?.bundleSpecificationDetails?.experienceType
  });

  const { cartReqParams, cartOptions } = getCartData({ cartItems, storeId, storeZip, zipCode: deliveryZip });

  const triggerAnalytics = () => {
    const output = {
      items: cartItems,
      product: bundle,
      anchorProductSku: itemId
    };
    tiggerAnalyticsCustomEvent(LAUNDRY_ADD_TO_CART, output);
  };

  return (
    <AddToCart
      cartOptions={cartOptions}
      cartReqParams={cartReqParams}
      outline
      onClick={triggerAnalytics}
      variant="secondary"
      small={false}
    >
      Add to Cart
    </AddToCart>
  );
};

LaundryAddToCart.propTypes = {
  bundle: shape({}).isRequired,
  products: arrayOf(shape({})).isRequired,
  accessories: arrayOf(shape({}))
};
LaundryAddToCart.defaultProps = {
  accessories: []
};